import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const STEPS = [
  {
    title: 'Customers Email You',
    body: 'acme@edgemail.cloud, or bring your own domain. Cloudflare Email Routing delivers it to a Durable Object — one per thread.',
  },
  {
    title: 'The Agent Investigates',
    body: 'Classifies, looks up the customer across your tools, and drafts a reply. Rate-limited and audited per ticket.',
  },
  {
    title: 'You Approve or Auto-Send',
    body: 'Every org sets its own confidence threshold. Replies thread correctly and live-update across your team.',
  },
];

export function HowItWorksSection() {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <section id="how" className="max-w-6xl mx-auto px-6 py-20">
      <div className="max-w-2xl mb-12">
        <div className="mono-label mb-3">How It Works</div>
        <h2 className="text-4xl md:text-5xl font-normal tracking-sub leading-heading">
          Inbound Email → Drafted Reply in Seconds.
        </h2>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.5fr] gap-12 items-start">
        {/* Left: Stepper List */}
        <div className="flex flex-col relative space-y-2">
          {/* Connecting line behind steps */}
          <div className="absolute left-[19px] top-8 bottom-8 w-[2px] bg-border z-0" />

          {STEPS.map((step, i) => {
            const isActive = activeStep === i;
            return (
              <button
                key={step.title}
                onClick={() => setActiveStep(i)}
                className="relative z-10 text-left p-6 rounded-lg transition-colors group cursor-pointer focus-visible:outline-none focus:ring-2 focus:ring-accent"
              >
                {/* Active Background Indicator using LayoutId for smooth sliding */}
                {isActive && (
                  <motion.div
                    layoutId="how-active-bg"
                    className="absolute inset-0 bg-surface border border-border rounded-lg -z-10 shadow-sm"
                    initial={false}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}

                <div className="flex gap-4">
                  {/* Step Number Bubble */}
                  <div className={`mt-0.5 w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-mono text-sm font-medium transition-colors border
                    ${isActive ? 'bg-accent text-white border-accent' : 'bg-background text-foreground/50 border-border group-hover:border-foreground/30'}`}
                  >
                    0{i + 1}
                  </div>
                  
                  {/* Content */}
                  <div>
                    <h3 className={`text-xl font-normal tracking-sub leading-heading mb-2 transition-colors ${isActive ? 'text-foreground' : 'text-foreground/70 group-hover:text-foreground'}`}>
                      {step.title}
                    </h3>
                    <AnimatePresence>
                      {isActive && (
                        <motion.p
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="text-sm leading-body text-foreground/75 overflow-hidden"
                        >
                          {step.body}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Right: Dynamic Interactive Presentation */}
        <div className="card h-[450px] lg:h-[550px] bg-muted relative overflow-hidden flex items-center justify-center border-border shadow-sm">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              className="absolute inset-0 flex items-center justify-center p-2 sm:p-6 w-full h-full"
              initial={{ opacity: 0, y: 15, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -15, scale: 0.98 }}
              transition={{ ease: "easeInOut", duration: 0.3 }}
            >
              {activeStep === 0 && <StepOneVisual />}
              {activeStep === 1 && <StepTwoVisual />}
              {activeStep === 2 && <StepThreeVisual />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Step Illustrations (Custom SVGs matching the brand geometry)
// ---------------------------------------------------------------------------

function StepOneVisual() {
  return (
    <div className="w-full h-full flex items-center justify-center">
      <img 
        src="/screen-recording-hq.gif" 
        alt="Screen recording of Customer Email interaction" 
        className="w-full h-full object-contain" 
      />
    </div>
  );
}

function StepTwoVisual() {
  return (
    <svg viewBox="0 0 400 300" className="w-full h-auto max-w-[340px] drop-shadow-sm" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="300" fill="none" />
      
      {/* Central Agent Node */}
      <g transform="translate(200 150)">
        <circle cx="0" cy="0" r="40" fill="#faf9f6" stroke="#111111" strokeWidth="1.5" />
        <circle cx="0" cy="0" r="14" fill="#ff5600" />
        
        {/* Orbiting tool calls */}
        <g stroke="#111111" strokeWidth="1.5" fill="none" opacity="0.3" strokeDasharray="3 4">
          <circle cx="0" cy="0" r="80" />
        </g>
        
        {/* Nodes on orbit */}
        <g transform="translate(-80 0)">
          <rect x="-16" y="-16" width="32" height="32" rx="4" fill="#ffffff" stroke="#111111" strokeWidth="1.5" />
          <path d="M-6 -2 L6 2 M-2 -6 L2 6" stroke="#111111" strokeWidth="1.5" />
        </g>
        <g transform="translate(56 -56)">
          <rect x="-16" y="-16" width="32" height="32" rx="4" fill="#ffffff" stroke="#111111" strokeWidth="1.5" />
          <circle cx="0" cy="0" r="6" stroke="#111111" strokeWidth="1.5" fill="none" />
        </g>
        <g transform="translate(56 56)">
          <rect x="-16" y="-16" width="32" height="32" rx="4" fill="#ffffff" stroke="#111111" strokeWidth="1.5" />
          <path d="M-6 -6 L0 6 L6 -6 Z" fill="#111111" />
        </g>
        
        {/* Ping pulses */}
        <circle cx="0" cy="0" r="28" fill="none" stroke="#ff5600" strokeWidth="2" opacity="0.4" />
      </g>
    </svg>
  );
}

function StepThreeVisual() {
  return (
    <svg viewBox="0 0 400 300" className="w-full h-auto max-w-[340px] drop-shadow-sm" xmlns="http://www.w3.org/2000/svg">
      {/* Background with subtle dots */}
      <defs>
        <pattern id="dot-grid-3" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.5" fill="#111111" opacity="0.08" />
        </pattern>
      </defs>
      <rect width="400" height="300" fill="url(#dot-grid-3)" />

      {/* Editor Modal containing the draft */}
      <g transform="translate(60 50)">
        <rect width="280" height="180" rx="6" fill="#faf9f6" stroke="#111111" strokeWidth="1.5" />
        <rect width="280" height="30" rx="6" fill="#ffffff" stroke="#111111" strokeWidth="1.5" />
        {/* Hidden bottom corners of header */}
        <rect y="20" width="280" height="10" fill="#ffffff" />
        <path d="M 0 30 L 280 30" stroke="#111111" strokeWidth="1.5" />
        
        <circle cx="16" cy="15" r="4" fill="#111111" />
        
        {/* Draft text */}
        <rect x="20" y="50" width="240" height="6" rx="2" fill="#111111" opacity="0.8" />
        <rect x="20" y="64" width="200" height="6" rx="2" fill="#111111" opacity="0.4" />
        <rect x="20" y="78" width="80" height="6" rx="2" fill="#111111" opacity="0.4" />
        
        {/* Highlighted Agent draft area */}
        <rect x="20" y="100" width="220" height="36" rx="4" fill="#ff5600" opacity="0.08" />
        <rect x="30" y="110" width="180" height="6" rx="2" fill="#ff5600" opacity="0.9" />
        <rect x="30" y="120" width="140" height="6" rx="2" fill="#ff5600" opacity="0.9" />
        
        {/* Buttons */}
        <rect x="180" y="146" width="80" height="22" rx="4" fill="#111111" />
        <rect x="190" y="155" width="60" height="4" rx="2" fill="#ffffff" />
        
        <rect x="90" y="146" width="80" height="22" rx="4" fill="none" stroke="#111111" strokeWidth="1.5" />
        <rect x="100" y="155" width="60" height="4" rx="2" fill="#111111" />
        
        {/* Human Cursor Vector */}
        <g transform="translate(196 150)">
          <path d="M0 0 L16 16 L8 16 L6 24 L2 22 L4 14 L-6 13 Z" fill="#ffffff" stroke="#111111" strokeWidth="1.5" strokeLinejoin="round" />
        </g>
      </g>
    </svg>
  );
}
